import { useEffect, useMemo } from "react";
import parse from "html-react-parser";
import { usePublication } from "../../../hooks/usePublication";
import { Section } from "../../../interfaces/Publication";
import Figure from "./Figure";
import SubSectionContent from "./SubSectionContent";
import { ButtonsRight } from "../../../interfaces/UI";
import { validateImageHttp } from "../../../helpers";
import { PodcastButton } from "./PlayerButton";
import HighlightableText from "./HighlatableText";
import { GlobalPodcastPlayer } from "./GlobalPlayer";

interface SectionContentProps {
  section: Section;
  openButtons: ButtonsRight;
  setOpenButtons: (state: ButtonsRight) => void;
}
declare const location: any;

const SectionContent = ({
  section,
  openButtons,
  setOpenButtons,
}: SectionContentProps) => {
  const { path } = usePublication();

  // Memoize expensive computations
  const isEditorial = useMemo(() => {
    if (section && section.title) {
      return section.title === "Editorial";
    }
    return false;
  }, [section?.title]);

  const imageSectionCover = useMemo(
    () =>
      validateImageHttp(section && section.cover ? section.cover : "", path),
    [section?.cover, path]
  );

  // Memoize parsed content to prevent re-parsing on every render
  const parsedTitle = useMemo(
    () => (section?.title ? parse(section.title) : null),
    [section?.title]
  );

  const parsedAbstract = useMemo(
    () => (section?.abstract ? parse(section.abstract) : null),
    [section?.abstract]
  );

  const parsedText = useMemo(
    () => (section?.text ? parse(section.text) : null),
    [section?.text]
  );

  // Memoize subsections array to prevent unnecessary re-renders
  const memoizedSubsections = useMemo(
    () => section?.subsections || [],
    [section?.subsections]
  );

  // Memoize the scroll effect to prevent recreation
  const scrollToHash = () => {
    const hash = location.hash;
    if (hash && hash.length > 0) {
      const element = document.querySelector(hash);
      element?.scrollIntoView();
    }
  };

  useEffect(() => {
    scrollToHash();
  }, [section]);

  // Memoize the subsections list to prevent re-creation on every render
  const subsectionsList = useMemo(() => {
    if (!memoizedSubsections.length) return null;

    return memoizedSubsections.map((subsection, index) => {

      return (
        <div key={`${subsection.id}-${index}`} style={{ position: "relative" }}>

          <SubSectionContent
            subSection={subsection}
            openButtons={openButtons}
            setOpenButtons={setOpenButtons}
            section={section}
            index={index}
          />
        </div>
      );
    });
  }, [memoizedSubsections, openButtons, setOpenButtons, section]);

  // Early return for loading state
  if (!section) {
    return (
      <div className="section-loading">
        <p>Loading ...</p>
      </div>
    );
  }

  return (
    <section
      id={`S${section.id}`}
      className="abstract"
      aria-label="sumario"
      data-scrollspy
    >
      <article className="section__article">
        {section.cover && !isEditorial && (
          <Figure path={`${imageSectionCover}`} />
        )}
        {parsedTitle && <h2>{parsedTitle}</h2>}
        {parsedAbstract && <div className="synopsis">{parsedAbstract}</div>}
        {parsedText && (
          <HighlightableText
            htmlContent={section.text}
            fragmentId={`section-${section.id}`}
            className="p-container gcontent first-para"
          />
        )}
      </article>

      {subsectionsList}
      <GlobalPodcastPlayer />
    </section>
  );
};

export default SectionContent;
